import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Signposts from './components/Signposts';
import { CMPX_HERO, CMPX_CONNECT, CMPX_SHARE, CMPX_PROV } from './components/ProductVisuals';
import { firePAEvent } from './utils/analytics';

/*
 * /cmpx — rebuilt 4 Sep 2026 as a product page: features, then a handover.
 *
 * It carried four persona sections, one per role, describing what each would
 * see. That is the landing page's job done twice, and it is the same overlap
 * that made /steadystride and /individuals 88% identical one level down.
 * Provisioning is the real behaviour, so saying it once covers all four roles
 * and the signpost block hands the reader on.
 *
 * The three features are universal but written so an HR reader lands on them,
 * since employers are the ICP:
 *   1. the record builds itself        — answers "how do you know?"
 *   2. data visibility is set by you   — answers "what will my people think?"
 *   3. provisioning by role            — answers "what do I actually get?"
 *
 * DELIBERATELY NOT HERE. The seven clinical-system integrations (Vald, Cliniko
 * and the rest), PROMs, the exercise library, MDT teams, payouts, sixteen
 * languages. All real, none of them this page's job, and the clinical systems
 * are a commercial disclosure. They are blog material.
 *
 * Load Rate (LRE) is named as a product term describing what the data shows,
 * never as a claim about what it proves, and the "our digital biomarker"
 * construction QA-05 removed does not appear. It links to /how-it-works until
 * it earns a page of its own.
 *
 * Anchors changed with this rebuild: #hr/#person/#physio/#research became
 * #connect/#sharing/#roles/#who. Eleven links across six files were repointed
 * in the same commit.
 */

const CmpxPage = () => {
    useEffect(() => {
        document.title = 'CMPX | PhysioActivity';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'The portal your movement data lands in. One record, visibility you set, and sections provisioned by role.');
    }, []);

    return (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap two">
                <div className="heromain">
                    <h1 className="on-dark">
                        <span className="setup">Your data,</span>
                        <span className="payoff">gathered into one record.</span>
                    </h1>
                    <p className="lede">
                        CMPX is where the data lands. Your phone, whatever you wear, and what
                        you logged, held as <b>one record</b> instead of five apps that do not
                        talk to each other.
                    </p>
                    <p className="nerve">
                        Most of it arrives on its own, and the rest takes seconds to add. Either
                        way the record is still there in twelve weeks.
                    </p>
                    <div className="cta-row">
                        <a
                            className="btn btn-t"
                            href="https://portal.physioactivity.com/"
                            onClick={() => firePAEvent('outbound_portal', { source: 'cmpx_hero' })}
                        >
                            Log in
                        </a>
                    </div>
                </div>
                <div className="herovis">
                    {CMPX_HERO}
                    <p className="cap">Illustrative. Not a real record.</p>
                </div>
            </div>
        </section>

        <section className="white" id="connect">
            <div className="wrap">
                <div className="featrow rev">
                    <div className="ftext">
                        <h2>
                            <span className="setup">Connect what you</span>
                            <span className="payoff">already use.</span>
                        </h2>
                        <p className="lede">
                            Add a watch or an app you already wear and it lands in the same
                            record, so <b>what you did sits against what your programme asked
                            for</b>. That pattern is read as{' '}
                            <Link to="/how-it-works"><b>Load Rate (LRE)</b></Link>.
                        </p>
                        <ul className="fpts">
                            <li>Apple Health, Health Connect, Samsung Health, Garmin, Fitbit</li>
                            <li><Link to="/steadystride">SteadyStride</Link> captures the rest, and is where you log what a sensor cannot see</li>
                            <li>Progress measured against your programme, not against a general target</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {CMPX_CONNECT}
                        <p className="cap">Illustrative.</p>
                    </div>
                </div>
            </div>
        </section>

        <section className="tint" id="sharing">
            <div className="wrap">
                <div className="featrow">
                    <div className="ftext">
                        <h2>
                            <span className="setup">Data visibility,</span>
                            <span className="payoff">set by you.</span>
                        </h2>
                        <p className="lede">
                            The record is yours, and so is the decision about who sees it. Open
                            it to your physiotherapist, or to any clinician, and <b>close it
                            again whenever you want</b>.
                        </p>
                        <ul className="fpts">
                            <li>You choose who sees it, and for how long</li>
                            <li>They can look, not change, and access closes on its own</li>
                            <li>Your employer never sees any of it, whoever is paying</li>
                        </ul>
                        <p className="quiet">
                            What an employer can and cannot see is set out in full in the{' '}
                            <Link to="/trust-centre">trust centre</Link>.
                        </p>
                    </div>
                    <div className="fvis">
                        {CMPX_SHARE}
                        <p className="cap">Illustrative.</p>
                    </div>
                </div>
            </div>
        </section>

        <section className="white" id="roles">
            <div className="wrap">
                <div className="featrow rev">
                    <div className="ftext">
                        <h2>
                            <span className="setup">Everyone has an account.</span>
                            <span className="payoff">Not everyone has the same one.</span>
                        </h2>
                        <p className="lede">
                            Everyone starts with the same default: your own record, your sources
                            and your sharing. <b>Anything beyond that is a role, and it has to be
                            granted.</b>
                        </p>
                        <ul className="fpts">
                            <li>HR and reward: a Workforce section, aggregate only, never individuals</li>
                            <li>Physiotherapists: Clients and Studio</li>
                            <li>Researchers: a Research section, under consent given separately</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {CMPX_PROV}
                        <p className="cap">The same navigation, three provisionings.</p>
                    </div>
                </div>
            </div>
        </section>

        <Signposts />

        <section className="s-promise" id="access">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">One portal.</span>
                    <span className="payoff">Three ways in.</span>
                </h2>
                <p className="lede">
                    If you use the app, your account is already there. Employers are set up with
                    us directly. Physiotherapists apply, and we verify registration first.
                </p>
                <div className="cta-row">
                    <a
                        className="btn btn-t"
                        href="https://portal.physioactivity.com/"
                        onClick={() => firePAEvent('outbound_portal', { source: 'cmpx_close' })}
                    >
                        Log in
                    </a>
                    <Link className="btn btn-o" to="/contact">Request access</Link>
                </div>
                <p className="quiet">
                    Not sure which of those you are? <Link to="/contact">Talk to us</Link>.
                </p>
            </div>
        </section>

    </div>
    );
};

export default CmpxPage;
